/**
 * Liens parent ⇄ enfant (actifs + invitations en attente) du parent connecté.
 * Une seule requête partagée par l'Accueil et « Mes enfants » (même clé de
 * cache `parent-children`, invalidée par les mutations d'abonnement/liaison).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type ParentChildLink = {
  link_id: string;
  status: 'pending' | 'active' | 'revoked';
  student: { id: string; display_name: string | null; avatar_url: string | null } | null;
};

export function useParentChildLinks(parentId: string) {
  return useQuery({
    queryKey: ['parent-children', parentId],
    enabled: !!parentId,
    staleTime: 60_000,
    queryFn: async (): Promise<ParentChildLink[]> => {
      const { data, error } = await supabase
        .from('parent_links')
        .select(
          'id, status, student:profiles!parent_links_student_id_fkey(id, display_name:visible_name, avatar_url)',
        )
        .eq('parent_id', parentId)
        .neq('status', 'revoked')
        .order('invited_at', { ascending: false });
      if (error) throw error;
      type Raw = {
        id: string;
        status: ParentChildLink['status'];
        student: ParentChildLink['student'];
      };
      return ((data ?? []) as unknown as Raw[]).map((l) => ({
        link_id: l.id,
        status: l.status,
        student: l.student,
      }));
    },
  });
}
